import { useEffect, useRef, useState } from "react";

// The slowest a failing poll backs off to.
export const MAX_BACKOFF_MS = 60000;
// A tick that hasn't settled by now counts as a failure. Without it one
// hung request (a laptop waking on a new network) stopped the loop for
// good while the page still said "Live" (review round, PR #327).
export const TICK_TIMEOUT_MS = 15000;

const hidden = () =>
  typeof document !== "undefined" && document.visibilityState === "hidden";

/**
 * Run `tick` every `intervalMs` while the page is visible — the live
 * board's heartbeat (projects/task-live-refresh, D5).
 *
 * - One tick at a time: the next is scheduled only after the last one
 *   settles, so a slow network can't stack requests.
 * - Visible only: a hidden tab stops ticking; coming back ticks at once,
 *   then resumes the cadence. Coming back online does the same.
 * - A tick that throws backs off (interval × 2ⁿ, capped at MAX_BACKOFF_MS)
 *   and reports "reconnecting"; the next success returns to "live".
 * - `resetKey`: changing it restarts the loop (with an immediate tick
 *   when `immediate` — a new board scope needs a baseline now).
 * - `immediate`: tick once as soon as it starts, not after one interval.
 * - A tick is handed `{ stale }`: once the loop has moved on (unmount, a
 *   new `resetKey`, or the tick timed out), `stale()` is true and the tick
 *   must not act on what it awaited — its closure belongs to the old
 *   world (a board scope switched mid-tick loaded the old scope's tasks
 *   under the new label; review round, PR #327). Check it after every
 *   await.
 * - A tick that doesn't settle within `timeoutMs` counts as a failure.
 *
 * `tick` is read through a ref, so a new closure each render doesn't
 * restart the timers.
 *
 * Returns `{ status }`: "live" | "reconnecting" | "off" (disabled).
 */
export default function usePoll(
  tick,
  {
    intervalMs = 5000,
    enabled = true,
    immediate = false,
    resetKey,
    timeoutMs = TICK_TIMEOUT_MS,
  } = {},
) {
  const tickRef = useRef(tick);
  tickRef.current = tick;
  const [failing, setFailing] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let timer = null;
    let running = false;
    let stopped = false;
    let failures = 0;

    const schedule = (ms) => {
      clearTimeout(timer);
      timer = setTimeout(run, ms);
    };

    async function run() {
      timer = null;
      if (stopped || running || hidden()) return;
      running = true;
      let abandoned = false;
      let deadline = null;
      try {
        const work = Promise.resolve().then(() =>
          tickRef.current({ stale: () => stopped || abandoned }),
        );
        work.catch(() => {}); // a tick abandoned by the deadline may still fail later
        await Promise.race([
          work,
          new Promise((_, reject) => {
            deadline = setTimeout(() => {
              abandoned = true;
              reject(new Error("poll tick timed out"));
            }, timeoutMs);
          }),
        ]);
        if (stopped) return;
        failures = 0;
        setFailing(false);
      } catch {
        if (stopped) return;
        failures += 1;
        setFailing(true);
      } finally {
        clearTimeout(deadline);
        running = false;
      }
      if (stopped || hidden()) return;
      schedule(
        failures === 0
          ? intervalMs
          : Math.min(intervalMs * 2 ** failures, MAX_BACKOFF_MS),
      );
    }

    // back in view (or back online): check now, then resume the cadence
    const wake = () => {
      if (stopped || hidden() || running) return;
      schedule(0);
    };

    document.addEventListener("visibilitychange", wake);
    window.addEventListener("online", wake);
    schedule(immediate ? 0 : intervalMs);

    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("online", wake);
    };
  }, [enabled, intervalMs, immediate, resetKey, timeoutMs]);

  // a fresh loop starts optimistic
  useEffect(() => {
    setFailing(false);
  }, [resetKey, enabled]);

  return { status: !enabled ? "off" : failing ? "reconnecting" : "live" };
}
